/**
 * Legs as the streets and roads they would follow, from Mapbox Directions -- the same service, and the
 * same public token, the web app's transport pickers use to route a drive.
 *
 * Two callers. A city trip's places (placeStops.ts) always get their walks and drives routed. A trip's
 * destinations only when a marketing render asks for it (`styles.roadRoutes`): a leg nobody planned
 * transport for is otherwise a straight line, and a route there is a guess at how the traveller went.
 * Fine for a video we make to show a route off, not for a traveller's own film of their own trip.
 */

const MAPBOX_TOKEN = "pk.eyJ1Ijoib21hcnMiLCJhIjoiY2w0NDN3OHRvMDAxazNkcXRzcG1kMmpyMCJ9.l6IL4HDt1K5dfcDAFnp-vg";

/** Past this a leg nobody planned is left as the arc it was: a road across a continent is not a guess worth making. */
export const MAX_ROAD_KM = 1500;

type Leg = { path: number[][]; vehicle?: string; [key: string]: any };
type Get = (url: string) => Promise<any>;

const getJson: Get = (url) => fetch(url).then((res) => (res.ok ? res.json() : null));

const km = (a: number[], b: number[]) => {
	const R = 6371;
	const dLat = ((b[1] - a[1]) * Math.PI) / 180;
	const dLng = ((b[0] - a[0]) * Math.PI) / 180;
	const h = Math.sin(dLat / 2) ** 2 + Math.cos((a[1] * Math.PI) / 180) * Math.cos((b[1] * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
	return 2 * R * Math.asin(Math.sqrt(h));
};

/**
 * One leg along a Mapbox route. The route starts and ends on the nearest road, so the leg's own ends
 * are put back at both sides; otherwise the line would stop short of its pins. Null when the request
 * fails or finds nothing, and the caller keeps the leg it had -- a render never fails for a route.
 */
const routed = async (leg: Leg, profile: string, get: Get): Promise<number[][] | null> => {
	const from = leg.path[0];
	const to = leg.path[leg.path.length - 1];
	try {
		const data = await get(
			`https://api.mapbox.com/directions/v5/mapbox/${profile}/${from[0]},${from[1]};${to[0]},${to[1]}?geometries=geojson&overview=full&steps=false&access_token=${MAPBOX_TOKEN}`
		);
		const route = data?.routes?.[0]?.geometry?.coordinates;
		return Array.isArray(route) && route.length >= 2 ? [from, ...route, to] : null;
	} catch {
		return null;
	}
};

const STREET_PROFILE: Record<string, string> = { walk: "walking", car: "driving" };

/** The legs between a city trip's places: walks along streets, drives along roads. */
export const withStreetRoutes = async <T extends Leg>(reelData: T[], get: Get = getJson): Promise<T[]> =>
	Promise.all(
		reelData.map(async (leg) => {
			const profile = leg.vehicle ? STREET_PROFILE[leg.vehicle] : undefined;
			if (!profile || (leg.path?.length ?? 0) < 2) return leg;
			const path = await routed(leg, profile, get);
			return path ? { ...leg, path } : leg;
		})
	);

/** Which Directions profile a destination leg gets, if any. Unplanned is driven; rail, sea and air keep their own lines. */
const roadProfile = (vehicle?: string) =>
	!vehicle || vehicle === "car" || vehicle === "bus" || vehicle === "taxi" ? "driving" : vehicle === "walk" ? "walking" : vehicle === "bike" ? "cycling" : undefined;

/**
 * For a marketing render: every destination leg that has no geometry of its own -- the two points the
 * app sends when no transport was planned, or a drive planned without a route -- follows the road
 * Mapbox finds. A leg with a real path, a flight, a train or a ferry is left exactly as it was, and so
 * is a leg longer than MAX_ROAD_KM or one Mapbox finds no road for (an island).
 */
export const withRoadRoutes = async <T extends Leg>(reelData: T[], get: Get = getJson): Promise<T[]> =>
	Promise.all(
		(reelData || []).map(async (leg) => {
			const profile = roadProfile(leg?.vehicle);
			if (!profile || leg?.path?.length !== 2 || km(leg.path[0], leg.path[1]) > MAX_ROAD_KM) return leg;
			const path = await routed(leg, profile, get);
			return path ? { ...leg, path, vehicle: leg.vehicle || "car" } : leg;
		})
	);
